import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { ProductCard } from '../components/ProductCard';
import { fetchProducts, Product, Category } from '../services/stripe';
import { useRecentlyViewed } from '../context/RecentlyViewedContext';
import { Theme } from '@mui/material/styles';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Products: React.FC = () => {
  const location = useLocation();
  const { addToRecentlyViewed } = useRecentlyViewed();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [productsByCategory, setProductsByCategory] = useState<Record<string, Product[]>>({});

  // Function to handle product view
  const handleProductView = useCallback((product: Product) => {
    console.log('Viewing product:', product);
    addToRecentlyViewed({
      ...product,
      category: product.metadata?.category || product.category || 'Uncategorized'
    });
  }, [addToRecentlyViewed]);

  // Add click handler to product card wrapper
  const ProductCardWrapper: React.FC<{ product: Product }> = ({ product }) => (
    <Box 
      onClick={() => handleProductView(product)}
      sx={{ 
        cursor: 'pointer',
        height: '100%',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-12px) scale(1.02)',
        },
      }}
    >
      <ProductCard
        id={product.id}
        productId={product.productId}
        name={product.name}
        price={product.price}
        description={product.description}
        image={product.image}
        images={product.images}
      />
    </Box>
  );

  useEffect(() => {
    let mounted = true;

    const loadProducts = async () => {
      try {
        setLoading(true);
        const data = await fetchProducts();
        if (mounted) {
          // Ensure products have categories
          const productsWithCategories = data.products.map((product: Product) => ({
            ...product,
            category: product.metadata?.category || product.category || 'Uncategorized'
          }));

          setProducts(productsWithCategories);
          setCategories(data.categories);

          // Group products by category
          const groupedProducts = productsWithCategories.reduce((acc: Record<string, Product[]>, product: Product) => {
            const category = product.metadata?.category || product.category || 'Uncategorized';
            if (!acc[category]) {
              acc[category] = [];
            }
            acc[category].push(product);
            return acc;
          }, {});

          console.log('Products by category:', groupedProducts); // Debug log
          setProductsByCategory(groupedProducts);

          // Set initial tab based on URL parameter
          const params = new URLSearchParams(location.search);
          const categoryParam = params.get('category');
          if (categoryParam) {
            const categoryIndex = data.categories.findIndex(
              (cat: Category) => cat.name.toLowerCase() === categoryParam.toLowerCase()
            );
            if (categoryIndex !== -1) {
              setSelectedTab(categoryIndex + 1); // Add 1 because index 0 is "All Products"
            }
          }

          setError(null);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load products');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadProducts();

    return () => {
      mounted = false;
    };
  }, [location.search]);

  // Reset loading state when component unmounts
  useEffect(() => {
    return () => {
      setLoading(true); // Reset loading state for next mount
    };
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: (theme: Theme) => theme.palette.primary.main,
          color: 'white',
          py: { xs: 6, md: 8 },
          mb: 6,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.4))',
            zIndex: 1,
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/products-hero.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: 'scale(1.1)',
            transition: 'transform 0.3s ease-out',
            '&:hover': {
              transform: 'scale(1.15)',
            },
          }}
        />
        <Container maxWidth="lg">
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              textAlign: 'center',
              maxWidth: 1000,
              mx: 'auto',
              px: 3,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2.5rem', md: '4rem' },
                mb: 3,
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                color: 'white',
                letterSpacing: '-0.02em',
              }}
            >
              Our Products
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                lineHeight: 1.6,
                maxWidth: 800,
                mx: 'auto',
                fontWeight: 'normal',
                textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
                color: 'white',
                fontSize: { xs: '1.1rem', md: '1.3rem' },
              }}
            >
              Discover our collection of premium products designed for your intimate moments
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Categories Tabs */}
      <Container maxWidth="lg">
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            mb: 4,
            '& .MuiTabs-root': {
              minHeight: 64,
            },
            '& .MuiTab-root': {
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
              minHeight: 64,
              px: 4,
              '&.Mui-selected': {
                color: (theme: Theme) => theme.palette.primary.main,
              },
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: 1.5,
            },
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            aria-label="product categories"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="All Products" />
            {categories.map((category, index) => (
              <Tab key={category.name} label={category.name} />
            ))}
          </Tabs>
        </Box>

        {/* Products Grid */}
        <TabPanel value={selectedTab} index={0}>
          <Grid container spacing={4}>
            {products.map((product) => (
              <Grid item key={product.id} xs={12} sm={6} md={4}>
                <ProductCardWrapper product={product} />
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {categories.map((category, index) => (
          <TabPanel key={category.name} value={selectedTab} index={index + 1}>
            <Grid container spacing={4}>
              {productsByCategory[category.name]?.map((product) => (
                <Grid item key={product.id} xs={12} sm={6} md={4}>
                  <ProductCardWrapper product={product} />
                </Grid>
              ))}
            </Grid>
          </TabPanel>
        ))}
      </Container>
    </Box>
  );
};

export default Products;
